/**
 * Quick Reference: what everything is, in one scrollable overlay.
 *
 * Every mode's (i) button opens this at that mode's entry. The requirement is
 * P0 for an unusual reason: in testing, teachers repeatedly reported not
 * knowing what a mode was for — and a tool for seven volunteers has no
 * training budget and no second first impression. The overlay is the manual,
 * so it is written like one page of a good manual: plain sentences, no
 * feature names that do not appear on screen, and the glyphs shown beside
 * their meanings rather than described.
 */

import { useEffect, useRef } from 'react';
import type { Mode } from './Shell.js';
import { VERBS } from '../read/Marks.js';

export type QuickRefEntry = Mode | 'general';

interface Section {
  id: QuickRefEntry;
  title: string;
  body: React.ReactNode;
}

/** A glyph shown beside its meaning, the way it appears in the interface. */
function Glyph({ mark, children }: { mark: string; children: React.ReactNode }) {
  return (
    <li className="flex items-baseline gap-3 py-0.5">
      <span aria-hidden className="clause-number w-6 shrink-0 text-center text-[13px]">
        {mark}
      </span>
      <span className="text-[13px]">{children}</span>
    </li>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return (
    <p className="register-reading mt-1 text-[14px]" style={{ lineHeight: 1.55 }}>
      {children}
    </p>
  );
}

const SECTIONS: Section[] = [
  {
    id: 'Read',
    title: 'Read',
    body: (
      <>
        <P>
          The agreement itself — 674 clauses, every one checked word for word against the signed
          document. Choose how much you see at once: <strong>Clause</strong> is one provision with
          its lettered paragraphs, <strong>Section</strong> is the whole numbered clause, and{' '}
          <strong>Part</strong> is everything under that Part.
        </P>
        <P>
          Selecting any text opens the six verbs. A mark you make is{' '}
          <strong>private until you publish it</strong> — the header counts what only you can see.
          The outline on the left can filter to only the clauses somebody has found something in.
        </P>
        <ul className="mt-2">
          <Glyph mark="△">A defective cross-reference — the clause cites something that is not there</Glyph>
          <Glyph mark="●">Published marks on this clause</Glyph>
          <Glyph mark="?">Questions attached to this clause</Glyph>
          <Glyph mark="□">Claims relying on this clause</Glyph>
          <Glyph mark="◇">On the protect list — something we must not lose</Glyph>
          <Glyph mark="∅">Silent — comparable agreements cover this; ours says nothing</Glyph>
        </ul>
      </>
    ),
  },
  {
    id: 'general',
    title: 'The six verbs',
    body: (
      <>
        <P>Select any text in the reader and choose what the selection is for:</P>
        <ul className="mt-2">
          {/*
            Read from VERBS, not typed out again.
            
            This listed 🖍 📝 ❓ 💬 ⚖ ▣ — the glyphs the action bar used before
            they were replaced with typographic ones. So the help overlay was
            teaching six symbols that no longer appear anywhere in the product,
            which is worse than no help: someone reads it, looks for a crayon,
            and concludes the bar is missing.
            
            Two copies of the same list will always drift, and this one drifted
            the first time the list changed. Taking them from the source means
            a seventh verb, or a changed glyph, arrives here on its own.
          */}
          {VERBS.map(({ verb, glyph, label, hint }) => (
            <Glyph key={verb} mark={glyph}>
              {label} — {hint.charAt(0).toLowerCase() + hint.slice(1)}
              {verb === 'claim'
                ? '. This is the gesture the whole tool is built around.'
                : ''}
            </Glyph>
          ))}
        </ul>
        <P>
          Notes and questions can be made <strong>without your name</strong> — the choice sits in
          the box where you write, and the server never sends an anonymous author&rsquo;s name to
          anyone. Claims are group work and always carry their drafter.
        </P>
      </>
    ),
  },
  {
    id: 'Ask',
    title: 'Ask',
    body: (
      <>
        <P>
          Grounded questions about the loaded documents. Every answer must cite the clause it
          stands on; an answer that cannot cite is suppressed rather than shown. Quotation marks
          are a promise — any quoted span that is not verbatim in the corpus loses its marks.
        </P>
        <P>
          Refusal is a valid answer: if the documents do not cover it, Ask says so and names what
          would have to be checked. It never guesses a clause number.{' '}
          <em>Not built yet — coming in the next phase.</em>
        </P>
      </>
    ),
  },
  {
    id: 'Claims',
    title: 'Claims',
    body: (
      <>
        <P>
          The bargaining position, one claim at a time. Each claim holds the exact words it
          amends (frozen when the claim was made, so a later redraft cannot quietly move the
          target), the proposed wording as a redline — deletions struck, insertions marked — the
          rationale, what it costs, and every position the employer has taken on it, dated and
          quoted.
        </P>
        <P>
          The wording carries a stamp: <span className="stamp stamp-ai">not yet checked</span>{' '}
          until a person has read it word for word, then{' '}
          <span className="stamp stamp-verified">checked</span>. Editing the words clears the
          tick — it belongs to the exact words that were checked.
        </P>
      </>
    ),
  },
  {
    id: 'Protect',
    title: 'Protect',
    body: (
      <P>
        The board of things already in the agreement that must not be lost — the conditions the
        employer would quietly trade away. A protect item is a claim of type Protect; the board
        is the view of them. <em>Not built yet — coming in the next phase.</em>
      </P>
    ),
  },
  {
    id: 'Ledgers',
    title: 'Ledgers',
    body: (
      <>
        <P>
          Three tables, one shell: the group&rsquo;s institutional memory. <strong>Questions</strong>{' '}
          triage into four lanes — answerable from the document, from the Award or NES, ask the
          employer, needs union advice — and each opens to its drafted answer with citations.{' '}
          <strong>Claims</strong> is the ranked list. <strong>Decisions</strong> records what was
          agreed, when, how, and what an earlier decision it replaces.
        </P>
        <P>
          Anyone can raise a question, with or without their name. Any representative can triage,
          because the lanes are shared work, not personal state.
        </P>
      </>
    ),
  },
  {
    id: 'Compare',
    title: 'Compare',
    body: (
      <>
        <P>
          This agreement beside the Award floor and the NSW public school Award, clause by
          clause. The public Award is the strongest comparator: same job, same state, same
          accreditation structure, published by government.
        </P>
        <P>
          One number matters everywhere: a period here is <strong>50 minutes</strong>; in the
          public system it is <strong>40</strong>. A cap copied across as &ldquo;28
          periods&rdquo; would be 280 minutes a week more teaching than the cap it copies.{' '}
          <em>Not built yet — the dock&rsquo;s comparator cards cover part of this today.</em>
        </P>
      </>
    ),
  },
  {
    id: 'Room',
    title: 'Room',
    body: (
      <P>
        The meeting view: what is on today, straw polls with aggregate results only — never who
        voted which way — and the decisions logged as they are made.{' '}
        <em>Not built yet — coming in the next phase.</em>
      </P>
    ),
  },
  {
    id: 'Verify',
    title: 'Verify',
    body: (
      <>
        <P>
          Where the workbench asks you to check its homework. Everything a model
          drafts — the comparisons against the Awards, the plain-language
          rewrites, the leverage notes — arrives here marked as unchecked, and
          stays unchecked until a representative reads both clauses and says
          otherwise.
        </P>
        <P>
          Three answers. <strong>Checked</strong> means you read the two clauses
          and the comparison is right. <strong>Correct it</strong> means it is
          close but the difference is stated wrongly — and your sentence
          replaces the model&rsquo;s. <strong>Not a real comparison</strong>{' '}
          means the two clauses are not about the same thing at all.
        </P>
        <P>
          Corrections are the valuable ones. Each is kept alongside what the
          model originally said, and together they are how the drafting gets
          better — so a correction is worth the minute it costs.
        </P>
      </>
    ),
  },
  {
    id: 'general',
    title: 'Provenance, and what to trust',
    body: (
      <>
        <P>Nothing produced by AI is allowed to look like a fact from the document:</P>
        <ul className="mt-2">
          <Glyph mark="◔">AI drafted, not yet verified by a person — treat as a lead, not a fact</Glyph>
          <Glyph mark="✓">Checked by a named person, word for word</Glyph>
          <Glyph mark="≈">Machine-matched — the closest passage, not an established equivalent</Glyph>
        </ul>
        <P>
          The clause text itself carries no stamp because it needs none: it is verified against
          the signed PDF in both directions — every word of the corpus is in the source, and
          every sentence of the source is in the corpus.
        </P>
        <P>
          Nothing here runs through school systems — not sign-in, not email, not storage. Private
          marks are enforced by the database, not by the screen: a mark you have not published
          does not leave the server for anyone else, and neither does the name on an anonymous
          one.
        </P>
      </>
    ),
  },
];

export function QuickRef({ entry, onClose }: { entry: QuickRefEntry; onClose: () => void }) {
  const opened = useRef<HTMLElement>(null);

  useEffect(() => {
    opened.current?.scrollIntoView({ block: 'start' });
  }, [entry]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // The first section matching the entry gets the scroll anchor; 'general'
  // entries land on the verbs, which is the most useful default.
  const anchorIndex = SECTIONS.findIndex((s) => s.id === entry);

  return (
    <div
      className="fixed inset-0 z-50 flex justify-center overflow-y-auto py-10"
      style={{ background: 'rgb(0 0 0 / 0.25)' }}
      onClick={onClose}
      role="dialog"
      aria-label="Quick reference"
    >
      <div
        className="h-fit w-full max-w-xl border px-8 py-6"
        style={{ background: 'var(--color-paper)', borderColor: 'var(--color-rule-strong)' }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-baseline justify-between">
          <h1 className="label m-0">Quick reference</h1>
          <button onClick={onClose} className="hit label underline">
            Close (esc)
          </button>
        </div>

        {SECTIONS.map((section, index) => (
          <section
            key={`${section.id}-${index}`}
            ref={index === anchorIndex ? opened : undefined}
            className="mt-6 border-t pt-4"
            style={{
              borderColor: 'var(--color-rule)',
              scrollMarginTop: 24,
              // The entry someone asked about reads first-class; the rest is
              // there for the scroll.
              background: index === anchorIndex ? 'var(--color-subtle)' : undefined,
              padding: index === anchorIndex ? '16px 12px 12px' : undefined,
            }}
          >
            <h2 className="register-reading m-0 text-[16px]" style={{ fontWeight: 600 }}>
              {section.title}
            </h2>
            {section.body}
          </section>
        ))}
      </div>
    </div>
  );
}
